import {
  arrayParam,
  locationUrlParts,
  numberParam,
  pointParam,
  stringParam,
  UrlParts,
  UrlQuery,
  booleanParam,
} from "../fetch";
import { Viewport } from "./hooks/useMapboxMap";
import { BaseStyleId } from "@cartographerio/atlas-map";
import {
  AccessToken,
  unsafeAccessToken,
  WorkspaceRef,
  unsafeWorkspaceRef,
} from "@cartographerio/types";
import { Option } from "@cartographerio/fp";

export interface AppQueryString {
  map?: string;
  apiUrl?: string;
  layers?: string[];
  base?: BaseStyleId;
  accessToken?: AccessToken;
  defaultWorkspace?: WorkspaceRef;
  scrollWheel?: boolean;
  viewport: Viewport;
}

function lookupApiUrl(param: string): string | undefined {
  switch (param) {
    case "dev":
    case "development":
      return "http://localhost:9000";
    case "beta":
      return "https://api.beta.cartographer.io";
    case "prod":
    case "production":
      return "https://api.cartographer.io";
    default:
      return undefined;
  }
}

export function orElse<A>(value: A | undefined, orElse: A): A {
  return value == null ? orElse : value;
}

export function mapNullable<A, B>(
  value: A | undefined,
  fn: (a: A) => B
): B | undefined {
  return value == null ? undefined : fn(value);
}

function parseBaseStyleId(str: string): Option<BaseStyleId> {
  const normalized = str.toLowerCase();
  return normalized === "terrain"
    ? Option.some("terrain")
    : normalized === "satellite"
    ? Option.some("satellite")
    : Option.none();
}

export function parseAppQueryString(
  location: Location,
  defaults: AppQueryString
): AppQueryString {
  const parts: UrlParts = locationUrlParts(location);
  const query: UrlQuery = parts.query || {};

  const map = stringParam(query, "map").getOrElse(() => defaults.map);

  const apiUrl = stringParam(query, "apiserver")
    .map(lookupApiUrl)
    .getOrElse(() => defaults.apiUrl);

  const layers = arrayParam(query, "layer", ",").getOrElse(
    () => defaults.layers
  );

  const base = stringParam(query, "base")
    .orElse(() => Option.wrap(defaults.base))
    .flatMap(parseBaseStyleId)
    .getOrUndefined();

  const accessToken = stringParam(query, "authorization")
    .map(unsafeAccessToken)
    .getOrUndefined();

  const defaultWorkspace = stringParam(query, "workspace")
    .map(unsafeWorkspaceRef)
    .getOrUndefined();

  const scrollWheel = booleanParam(query, "scrollwheel").getOrUndefined();

  const center = pointParam(query, "center").getOrElse(
    () => defaults.viewport.center
  );

  const zoom = numberParam(query, "zoom").getOrElse(
    () => defaults.viewport.zoom
  );

  const bearing = numberParam(query, "bearing").getOrElse(
    () => defaults.viewport.bearing
  );

  const pitch = numberParam(query, "pitch").getOrElse(
    () => defaults.viewport.pitch
  );

  return {
    map,
    apiUrl,
    layers,
    base,
    accessToken,
    defaultWorkspace,
    scrollWheel,
    viewport: {
      center,
      zoom,
      bearing,
      pitch,
    },
  };
}

// export function formatAppQueryString(query: AppQueryString): string {
//   let ans: [string, string][] = [];

//   if (query.map != null) {
//     ans = ans.concat([["map", encodeURIComponent(query.map)]]);
//   }

//   if (query.layers != null) {
//     ans = ans.concat([["map", encodeURIComponent(query.layers.join(","))]]);
//   }

//   if (query.viewport.center != null) {
//     ans = ans.concat([
//       [
//         "center",
//         encodeURIComponent(query.viewport.center.coordinates.join(","))
//       ]
//     ]);
//   }

//   if (query.viewport.zoom != null) {
//     ans = ans.concat([["zoom", `${query.viewport.zoom}`]]);
//   }

//   if (query.viewport.bearing != null) {
//     ans = ans.concat([["bearing", `${query.viewport.bearing}`]]);
//   }

//   if (query.viewport.pitch != null) {
//     ans = ans.concat([["pitch", `${query.viewport.pitch}`]]);
//   }

//   return "?" + ans.map(pair => pair.join("=")).join("&");
// }
